import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahrq6boh653ivfvo4b0";

interface ServerResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setFormError("");

    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，爆料速递会发到你的邮箱。");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("loading");
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();

      let parsed: ServerResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ServerResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setMessage("订阅成功，爆料速递会发到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : "提交失败，请稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full scroll-mt-20 bg-background-50 px-4 py-10 md:px-8 md:py-14">
      <div className="overflow-hidden rounded-lg border border-background-200/70">
        <div className="grid lg:grid-cols-2">
          <div className="bg-background-100 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-notification-3-line text-sm"></i>
              </span>
              每日速递
            </span>
            <h2 className="mt-3 text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              订阅爆料速递，吃瓜不迷路
            </h2>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              每天一封汇总邮件，把当天最值得关注的黑料学生爆料、实锤与被扒翻车瓜整理好发给你，
              只发重点，不刷屏。
            </p>
            <ul className="mt-5 flex flex-col gap-2.5">
              {["每天一封，随时可退订", "只汇总已标注核实状态的内容", "不会向第三方分享你的邮箱"].map(
                (text) => (
                  <li key={text} className="flex items-center gap-2.5 text-[13px] text-foreground-700">
                    <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                      <i className="ri-checkbox-circle-line text-base"></i>
                    </span>
                    {text}
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="border-t border-background-200/70 bg-background-50 p-6 md:p-10 lg:border-l lg:border-t-0">
            <form id="subscribe-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <label htmlFor="subscribe-email" className="text-xs font-medium text-foreground-700">
                邮箱地址
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5">
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  <input
                    id="subscribe-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                >
                  <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                  {status === "loading" ? "提交中" : "订阅速递"}
                </button>
              </div>

              <select
                name="focus"
                defaultValue="爆料速递"
                className="w-full cursor-pointer rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none"
              >
                <option value="爆料速递">最关注：爆料速递</option>
                <option value="实锤现场">最关注：实锤现场</option>
                <option value="翻车汇总">最关注：翻车汇总</option>
                <option value="社区讨论">最关注：社区讨论</option>
              </select>

              <input
                className="form-guard-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" ? (
                <p className="mt-1 flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-[13px] text-secondary-900">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-base"></i>
                  </span>
                  {message}
                </p>
              ) : null}

              {status === "error" ? (
                <p className="mt-1 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-[13px] text-primary-800">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-error-warning-fill text-base"></i>
                  </span>
                  {formError}
                </p>
              ) : null}

              <p className="mt-1 text-[11px] leading-relaxed text-foreground-400">
                提交即表示同意我们仅将邮箱用于发送爆料速递，可随时在邮件底部一键退订。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}